/**
 * Branches as the public sees them (M8-5, owner O39, D210): the website and the customer app show
 * only ACTIVE branches the admin web marks PUBLIC, with the names, addresses, phones and opening
 * hours kept in `locations`. Whatever is empty is left out — never a placeholder.
 *
 * Opening hours: 7 days, Saturday first (the Iraqi week); each day `null` (closed),
 * `{ open, close }` in Baghdad time, "HH:MM" — a close at or before the open is after midnight —
 * or `{ allDay: true }` (open 24 hours, owner 2026-10-04). The same rule is checked by the
 * database (`is_valid_opening_hours`, migrations 0018 and 0019).
 */
import { z } from 'zod';

import { formatPhone } from '../phone/phone.ts';
import { phoneSchema } from '../validation/issues.ts';

/** Saturday first: index 0 = Saturday … 6 = Friday. */
export const WEEK_DAYS = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const time = z.string().regex(HHMM);

export const TimedDaySchema = z
  .object({ open: time, close: time })
  .strict()
  .refine((d) => d.open !== d.close, { path: ['close'] });
export type TimedDay = z.infer<typeof TimedDaySchema>;

/** Open the whole day and night. */
export const AllDaySchema = z.object({ allDay: z.literal(true) }).strict();
export type AllDay = z.infer<typeof AllDaySchema>;

export const OpeningDaySchema = z.union([TimedDaySchema, AllDaySchema]);
export type OpeningDay = z.infer<typeof OpeningDaySchema>;

export const OpeningHoursSchema = z.array(OpeningDaySchema.nullable()).length(7);
export type OpeningHours = z.infer<typeof OpeningHoursSchema>;

export const isAllDay = (day: OpeningDay): day is AllDay => 'allDay' in day;

/** A close at or before the open: the branch closes after midnight. */
export function closesAfterMidnight(day: TimedDay): boolean {
  return day.close <= day.open;
}

const minutes = (hhmm: string): number => {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** How long a day is open, in minutes (after midnight counted): 12:00–02:00 → 840. */
export function openMinutes(day: TimedDay): number {
  return (minutes(day.close) - minutes(day.open) + 1440) % 1440 || 1440;
}

const sameDay = (a: OpeningDay | null, b: OpeningDay | null): boolean => {
  if (a === null || b === null) return a === b;
  if (isAllDay(a) || isAllDay(b)) return isAllDay(a) && isAllDay(b);
  return a.open === b.open && a.close === b.close;
};

/** Days with the same hours next to each other, for showing: "السبت – الأربعاء 12:00 – 02:00". */
export interface OpeningGroup {
  readonly from: WeekDay;
  readonly to: WeekDay;
  /** null: closed on these days. */
  readonly hours: OpeningDay | null;
}

export function groupOpeningHours(hours: OpeningHours): OpeningGroup[] {
  const groups: { from: number; to: number; hours: OpeningDay | null }[] = [];
  hours.forEach((day, i) => {
    const last = groups.at(-1);
    if (last !== undefined && sameDay(last.hours, day)) last.to = i;
    else groups.push({ from: i, to: i, hours: day });
  });
  return groups.map((g) => ({
    from: WEEK_DAYS[g.from] ?? 'sat',
    to: WEEK_DAYS[g.to] ?? 'fri',
    hours: g.hours,
  }));
}

/** One branch in `GET /api/public/branches`. Empty fields are absent, not null. */
export const PublicBranchSchema = z.object({
  code: z.string(),
  nameAr: z.string().min(1),
  nameEn: z.string().min(1),
  addressAr: z.string().min(1).optional(),
  addressEn: z.string().min(1).optional(),
  /** E.164; shown with `formatPhone`. Empty list when none is given. */
  phones: z.array(z.string()),
  // Hours a client cannot read (a newer form from a newer server) count as not given, so one
  // branch's hours never empty the whole list in an older app.
  openingHours: OpeningHoursSchema.optional().catch(undefined),
});
export type PublicBranch = z.infer<typeof PublicBranchSchema>;

export const PublicBranchesSchema = z.object({ branches: z.array(PublicBranchSchema) });
export type PublicBranches = z.infer<typeof PublicBranchesSchema>;

/** What the admin web sends to change a location's public information. */
export const LocationPublicInfoSchema = z
  .object({
    nameAr: z.string().trim().min(1).max(80),
    nameEn: z.string().trim().min(1).max(80),
    addressAr: z.string().trim().max(200).nullable(),
    addressEn: z.string().trim().max(200).nullable(),
    /** Public numbers (owner rule O17: Iraqi mobiles, or a real foreign number with +); E.164 out. */
    phones: z.array(phoneSchema).max(3),
    openingHours: OpeningHoursSchema.nullable(),
    isPublic: z.boolean(),
  })
  .strict();

// ── Showing a branch (the website and the customer app, M8-5) ─────────────────

/** The words a view needs, in the page's language (from @dob/i18n: `week.*`, `site.am|pm|closed`). */
export interface BranchWords {
  readonly week: Readonly<Record<WeekDay, string>>;
  readonly am: string;
  readonly pm: string;
  readonly closed: string;
  /** «مفتوح 24 ساعة» / "Open 24 hours". */
  readonly allDay: string;
}

export interface BranchPhoneView {
  /** "0772 835 0100" — to be shown left to right in both languages. */
  readonly text: string;
  readonly href: string;
}

export interface BranchHoursLine {
  readonly days: string;
  /** "12:00 م – 2:00 ص", or the word for closed. */
  readonly time: string;
}

export interface BranchView {
  readonly code: string;
  readonly name: string;
  readonly address?: string;
  readonly phones: readonly BranchPhoneView[];
  readonly hours?: readonly BranchHoursLine[];
}

/** "16:00" → "4:00 م" / "4:00 PM"; "00:30" → "12:30 ص" / "12:30 AM". */
export function formatTime(hhmm: string, words: Pick<BranchWords, 'am' | 'pm'>): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour)}:${String(m).padStart(2, '0')} ${h < 12 ? words.am : words.pm}`;
}

/** Only what exists: no address, phone or hours line when it is missing (owner O39). */
export function branchView(
  branch: PublicBranch,
  lang: 'ar' | 'en',
  words: BranchWords,
): BranchView {
  const address = lang === 'ar' ? branch.addressAr : branch.addressEn;
  const hours = branch.openingHours
    ? groupOpeningHours(branch.openingHours).map((g) => ({
        days: g.from === g.to ? words.week[g.from] : `${words.week[g.from]} – ${words.week[g.to]}`,
        time:
          g.hours === null
            ? words.closed
            : isAllDay(g.hours)
              ? words.allDay
              : `${formatTime(g.hours.open, words)} – ${formatTime(g.hours.close, words)}`,
      }))
    : undefined;
  return {
    code: branch.code,
    name: lang === 'ar' ? branch.nameAr : branch.nameEn,
    ...(address ? { address } : {}),
    phones: branch.phones.map((e164) => ({ text: formatPhone(e164), href: `tel:${e164}` })),
    ...(hours ? { hours } : {}),
  };
}

// ── Hours that look like a mistake (the admin web warns before saving, owner 2026-10-04) ──────

export type HoursWarningKind = 'short' | 'opensAtNight' | 'almostAllDay';

export interface HoursWarning {
  readonly day: WeekDay;
  readonly kind: HoursWarningKind;
  readonly open: string;
  readonly close: string;
}

/**
 * Days whose hours look unusual: open less than an hour (e.g. 23:59–00:00), opening between
 * midnight and 05:00 (e.g. 00:00–11:00, typed as "12:00 AM"), or open 20 hours or more without
 * being marked "open 24 hours". Only a warning: the person may save anyway.
 */
export function unusualHours(hours: OpeningHours): HoursWarning[] {
  const found: HoursWarning[] = [];
  hours.forEach((day, i) => {
    if (day === null || isAllDay(day)) return;
    const weekDay = WEEK_DAYS[i] ?? 'sat';
    const base = { day: weekDay, open: day.open, close: day.close };
    const length = openMinutes(day);
    if (length < 60) found.push({ ...base, kind: 'short' });
    else if (length >= 20 * 60) found.push({ ...base, kind: 'almostAllDay' });
    if (minutes(day.open) < 5 * 60) found.push({ ...base, kind: 'opensAtNight' });
  });
  return found;
}

/**
 * A time typed in the 24-hour form, made exact: "14" → "14:00", "930" → "09:30", "9:5" → null
 * (minutes need two digits), Arabic-Indic digits and "." accepted. null when it is not a time.
 */
export function normalizeTimeInput(text: string): string | null {
  const latin = text
    .trim()
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[.\u066B\u066C٫]/g, ':')
    .replace(/\s+/g, '');
  let h: number;
  let m: number;
  let match = /^(\d{1,2})$/.exec(latin);
  if (match) {
    h = Number(match[1]);
    m = 0;
  } else if (
    (match = /^(\d{1,2}):(\d{2})$/.exec(latin)) ||
    (match = /^(\d{1,2})(\d{2})$/.exec(latin))
  ) {
    h = Number(match[1]);
    m = Number(match[2]);
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
